import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Breadcrumbs, H5 } from '../../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row, Input, FormGroup, Label } from 'reactstrap';
import DataTable from '../../../Common/DataTable';
import { PlusCircle, Edit, User, File, Trash2 } from 'react-feather';
import ExpenseDataModal from './ExpenseDataModal';
import DeleteConfirmationModal from '../../../Common/Modals/DeleteConfirmationModal';
import { confirmDelete, fetchTableData } from '../../../../Api/ApiMethods';
import { useNavigate } from 'react-router-dom';

const Expenses = () => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [clearSelectedRows, setClearSelectedRows] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [expenseTypeSearch, setExpenseTypeSearch] = useState('');
    const [startDateSearch, setStartDateSearch] = useState('');
    const [endDateSearch, setEndDateSearch] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const expenseApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense';

    const logSetData = (newData) => {
        console.log("Expenses.jsx: setData CALLED with new data length:", newData?.length, newData);
        setData(newData);
    };

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    const handleExpenseTypeSearchChange = (event) => {
        event.preventDefault();
        setExpenseTypeSearch(event.target.value);
        setPage(1);
    };

    const handleStartDateChange = (event) => {
        setStartDateSearch(event.target.value);
        setPage(1);
    };

    const handleEndDateChange = (event) => {
        setEndDateSearch(event.target.value);
        setPage(1);
    };

    useEffect(() => {
        let isMounted = true;
        const loadExpenses = async () => {
            setIsLoading(true);
            try {
                const queryParams = new URLSearchParams();
                if (expenseTypeSearch?.trim()) {
                    queryParams.append('expenseType', expenseTypeSearch.trim());
                }
                if (startDateSearch) {
                    queryParams.append('startDate', startDateSearch);
                }
                if (endDateSearch) {
                    queryParams.append('endDate', endDateSearch);
                }

                const apiUrl = queryParams.toString()
                    ? `${expenseApiUrl}?${queryParams.toString()}`
                    : expenseApiUrl;

                await fetchTableData({
                    apiEndpoint: apiUrl,
                    page,
                    pageSize,
                    search: null,
                    setDataCallback: logSetData,
                    setTotalRowsCallback: setTotalRows,
                });
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadExpenses();

        return () => {
            isMounted = false;
        };
    }, [page, pageSize, expenseTypeSearch, startDateSearch, endDateSearch, expenseApiUrl]);

    const toggleDeleteModal = () => setDeleteModal(!deleteModal);

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setTimeout(() => setSelectedExpense(null), 300);
        }
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedExpense(null);
        setModalOpen(true);
    };

    const openModifyModal = (expense) => {
        setModalMode('modify');
        setSelectedExpense(expense);
        setModalOpen(true);
    };

    const handleCallForm = (row) => {
        const callFormId = row.callForm?.[0];

        if (callFormId) {
            navigate(`/callform/${callFormId}`);
        } else {
            console.warn("Call form ID not found or structure unexpected in row:", row);
        }
    };

    const handleCustomerDetail = (customerId) => {
        navigate(`/customer/${customerId}`);
    };

    const refreshExpenseData = () => {
        console.log("Expenses.jsx: refreshExpenseData CALLED. Current state:", { page, pageSize, expenseTypeSearch, startDateSearch, endDateSearch });
        setIsLoading(true);

        const queryParams = new URLSearchParams();
        if (expenseTypeSearch?.trim()) {
            queryParams.append('expenseType', expenseTypeSearch.trim());
        }
        if (startDateSearch) {
            queryParams.append('startDate', startDateSearch);
        }
        if (endDateSearch) {
            queryParams.append('endDate', endDateSearch);
        }

        const apiUrl = queryParams.toString()
            ? `${expenseApiUrl}?${queryParams.toString()}`
            : expenseApiUrl;

        fetchTableData({
            apiEndpoint: apiUrl,
            page,
            pageSize,
            search: null,
            setDataCallback: logSetData,
            setTotalRowsCallback: setTotalRows,
        }).finally(() => setIsLoading(false));
    };

    const renderCellWithTooltip = (row, field, defaultValue = '') => {
        const value = row[field] || defaultValue;
        return (
            <div
                title={value}
                style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '150px',
                }}
            >
                {value}
            </div>
        );
    };

    const renderCallFormLink = (row) => {
        const callFormId = row.callForm?.[0];
        return callFormId ? (
            <div className="btn btn-outline-info" onClick={() => handleCallForm(row)} title="Kaza Dosyasına Git">
                <File size={18} />
            </div>
        ) : '-';
    };

    const columns = [
        { name: 'Masraf Numarası', selector: row => row.expenseNumber || '', sortable: true, width: '160px' },
        {
            name: 'Masraf Türü',
            selector: row => row.expenseType || '',
            sortable: true,
            cell: row => renderCellWithTooltip(row, 'expenseType'),
            width: '150px'
        },
        { name: 'Tutar', selector: row => row.amount ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(row.amount) : '', sortable: true, width: '120px' },
        { name: 'Tarih ve Saat', selector: row => row.date ? new Date(row.date).toLocaleString() : '', sortable: true, width: '180px' },
        // { name: 'Durum', selector: row => row.paymentStatus || '', sortable: true, width: '140px' },
        {
            name: 'Fatura No',
            selector: row => row.invoiceNumber || '',
            sortable: true,
            cell: row => renderCellWithTooltip(row, 'invoiceNumber'),
            width: '150px'
        },
        {
            name: 'Açıklama',
            selector: row => row.description || '',
            sortable: true,
            cell: row => renderCellWithTooltip(row, 'description'),
            grow: 2
        },
        {
            name: 'Müşteri',
            cell: row => {
                const customerId = Array.isArray(row.customer)
                    ? row.customer[0]
                    : (typeof row.customer === 'object' && row.customer !== null ? row.customer.id : null);

                return customerId ? (
                    <div className="btn btn-outline-primary" onClick={() => handleCustomerDetail(customerId)} title="Müşteri Detayı">
                        <User size={18} />
                    </div>
                ) : '-';
            },
            ignoreRowClick: true,
            right: true,
            button: true,
        },
        {
            name: 'Çağrı Formu',
            cell: renderCallFormLink,
            ignoreRowClick: true,
            right: true,
            button: true,
        },
        {
            name: 'Düzenle',
            cell: (row) => (
                <div
                    className="btn btn-outline-warning"
                    onClick={() => openModifyModal(row)}
                    title="Düzenle"
                >
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true,
        },
    ];

    return (
        <>
            <Breadcrumbs parent='Sayfalar' title='Masraflar' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Masraflar</H5>
                                <span>Mevcut olan masraflar aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <Row className="g-3 mb-3 align-items-center">
                                    <Col md="3" sm="6">
                                        <FormGroup>
                                            <Label>Masraf Türü</Label>
                                            <Input
                                                type="select"
                                                value={expenseTypeSearch}
                                                onChange={handleExpenseTypeSearchChange}
                                            >
                                                <option value="">Seçiniz</option>
                                                <option value="NOTER ÜCRETİ">NOTER ÜCRETİ</option>
                                                <option value="ARACI ÖDEMESİ">ARACI ÖDEMESİ</option>
                                                <option value="SATIN ALMA BEDELİ">SATIN ALMA BEDELİ</option>
                                                <option value="360 SORGU BEDELİ">360 SORGU BEDELİ</option>
                                                <option value="EKSPER FATURASI">EKSPER FATURASI</option>
                                                <option value="KEP BAŞVURU">KEP BAŞVURU</option>
                                                <option value="TAHKİM HARCI">TAHKİM HARCI</option>
                                                <option value="TAHKİM BAŞVURU ÜCRETİ ÖDEMESİ">TAHKİM BAŞVURU ÜCRETİ ÖDEMESİ</option>
                                                <option value="BİLİRKİŞİ ÖDEMESİ">BİLİRKİŞİ ÖDEMESİ</option>
                                                <option value="ISLAH ÖDEMESİ">ISLAH ÖDEMESİ</option>
                                                <option value="İTİRAZ ÖDEMESİ">İTİRAZ ÖDEMESİ</option>
                                                <option value="KARŞI VEKALET ÖDEMESİ">KARŞI VEKALET ÖDEMESİ</option>
                                                <option value="BARO PULU">BARO PULU</option>
                                                <option value="TAKİP AÇILIŞ MASRAFI">TAKİP AÇILIŞ MASRAFI</option>
                                                <option value="HACİZ MASRAFI">HACİZ MASRAFI</option>
                                                <option value="TAHSİL HARCI">TAHSİL HARCI</option>
                                                <option value="CEZAEVİ HARCI">CEZAEVİ HARCI</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    <Col md="3" sm="6">
                                        <FormGroup>
                                            <Label>Arama Başlangıç Tarihi</Label>
                                            <Input
                                                type="date"
                                                value={startDateSearch}
                                                onChange={handleStartDateChange}
                                                max={endDateSearch || undefined}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3" sm="6">
                                        <FormGroup>
                                            <Label>Arama Bitiş Tarihi</Label>
                                            <Input
                                                type="date"
                                                value={endDateSearch}
                                                onChange={handleEndDateChange}
                                                min={startDateSearch || undefined}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3" sm="6" className="d-flex justify-content-end align-items-center">
                                        <div className="btn btn-outline-primary ms-auto" onClick={openAddModal} style={{ height: '38px', display: 'inline-flex', alignItems: 'center' }}>
                                            <PlusCircle size={18} className="me-1" />
                                            {' Masraf Ekle'}
                                        </div>
                                        {(selectedRows.length !== 0) &&
                                            <div className='btn btn-outline-danger ms-2' onClick={toggleDeleteModal} style={{ height: '38px', display: 'inline-flex', alignItems: 'center' }}>
                                                <Trash2 size={18} className="me-1" />
                                                {' Seçilenleri Sil'}
                                            </div>
                                        }
                                    </Col>
                                </Row>

                                <div style={{ height: "40px" }}></div>

                                <DataTable
                                    columns={columns}
                                    data={data}
                                    onSelectedRowsChange={handleRowSelected}
                                    pagination
                                    paginationServer
                                    selectableRows
                                    clearSelectedRows={clearSelectedRows}
                                    paginationTotalRows={totalRows}
                                    onChangePage={page => setPage(page)}
                                    onChangeRowsPerPage={pageSize => { setPage(1); setPageSize(pageSize); }}
                                    progressPending={isLoading}
                                    progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                    noDataComponent={isLoading ? null : <div className="p-4 text-center">Masraf bulunamadı.</div>}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <ExpenseDataModal
                apiEndpoint={expenseApiUrl}
                isOpen={modalOpen}
                toggle={toggleModal}
                mode={modalMode}
                selectedExpense={selectedExpense}
                refreshData={refreshExpenseData}
                customerId={null}
                autoAssignCustomer={false}
                accidentFile={[]}
            />
            <DeleteConfirmationModal
                isOpen={deleteModal}
                toggle={toggleDeleteModal}
                itemsToDelete={selectedRows}
                displayKey="expenseNumber"
                onConfirm={() =>
                    confirmDelete({
                        apiEndpoint: expenseApiUrl,
                        selectedItems: selectedRows,
                        toggleModalCallback: toggleDeleteModal,
                        clearSelectionCallback: () => {
                            setSelectedRows([]);
                            setClearSelectedRows(!clearSelectedRows);
                        },
                        refreshDataCallback: () =>
                            fetchTableData({
                                apiEndpoint: (() => {
                                    const queryParams = new URLSearchParams();
                                    if (expenseTypeSearch?.trim()) {
                                        queryParams.append('expenseType', expenseTypeSearch.trim());
                                    }
                                    if (startDateSearch) {
                                        queryParams.append('startDate', startDateSearch);
                                    }
                                    if (endDateSearch) {
                                        queryParams.append('endDate', endDateSearch);
                                    }
                                    return queryParams.toString()
                                        ? `${expenseApiUrl}?${queryParams.toString()}`
                                        : expenseApiUrl;
                                })(),
                                page,
                                pageSize,
                                search: null,
                                setDataCallback: logSetData,
                                setTotalRowsCallback: setTotalRows,
                            }),
                    })
                }
            />
        </>
    );
};

export default Expenses;
